import { ProyectoMock } from '../../../mocks/proyectos';
import { AuthUser } from '../../../hooks/useAuth';

interface Props {
  user: AuthUser;
  proyectos: ProyectoMock[];
  onNuevoProyecto: () => void;
}

const ESTADO_CONFIG: Record<string, { color: string; icon: string; label: string }> = {
  'Pendiente evaluación': { color: 'bg-amber-100 text-amber-700 border-amber-200', icon: 'ri-time-line', label: 'Pendiente evaluación' },
  'En evaluación': { color: 'bg-blue-100 text-blue-700 border-blue-200', icon: 'ri-microscope-line', label: 'En evaluación' },
  'Aprobado': { color: 'bg-teal-100 text-teal-700 border-teal-200', icon: 'ri-checkbox-circle-line', label: 'Aprobado' },
  'Con correcciones': { color: 'bg-orange-100 text-orange-700 border-orange-200', icon: 'ri-edit-circle-line', label: 'Con correcciones' },
  'No pertinente': { color: 'bg-red-100 text-red-700 border-red-200', icon: 'ri-close-circle-line', label: 'No pertinente' },
  'En ejecución': { color: 'bg-teal-100 text-teal-700 border-teal-200', icon: 'ri-play-circle-line', label: 'En ejecución' },
  'Finalizado': { color: 'bg-slate-100 text-slate-600 border-slate-200', icon: 'ri-flag-line', label: 'Finalizado' },
};

function EstadoBadge({ estado }: { estado: string }) {
  const cfg = ESTADO_CONFIG[estado] || { color: 'bg-slate-100 text-slate-600 border-slate-200', icon: 'ri-question-line', label: estado };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${cfg.color}`}>
      <span className="w-3.5 h-3.5 flex items-center justify-center"><i className={cfg.icon} /></span>
      {cfg.label}
    </span>
  );
}

export default function InvestigadorPanel({ user, proyectos, onNuevoProyecto }: Props) {
  const misProyectos = proyectos.filter(
    p => p.email?.toLowerCase() === user.email.toLowerCase() ||
         p.investigador?.toLowerCase().includes(user.nombre.toLowerCase())
  );

  const tieneEvaluaciones = (p: ProyectoMock) =>
    (p.evaluaciones && p.evaluaciones.length > 0) ||
    (p.scores_eval && Object.keys(p.scores_eval).length > 0);

  return (
    <div>
      {/* Welcome */}
      <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 flex items-center justify-center bg-teal-600 rounded-full">
              <i className="ri-user-3-line text-white text-lg" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800">Bienvenido/a, {user.nombre}</h2>
              <p className="text-slate-500 text-xs">{user.email}</p>
            </div>
          </div>
          <p className="text-slate-500 text-sm ml-13">
            Aquí puede ver el estado de sus proyectos presentados y las evaluaciones del Comité.
          </p>
        </div>
        <button
          onClick={onNuevoProyecto}
          className="whitespace-nowrap flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors cursor-pointer"
        >
          <span className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line" /></span>
          Presentar nuevo proyecto
        </button>
      </div>

      {/* Stats */}
      {misProyectos.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          {[
            { label: 'Total presentados', value: misProyectos.length, icon: 'ri-file-text-line', color: 'text-teal-600 bg-teal-50' },
            { label: 'En evaluación', value: misProyectos.filter(p => p.estado === 'En evaluación' || p.estado === 'Pendiente evaluación').length, icon: 'ri-microscope-line', color: 'text-amber-600 bg-amber-50' },
            { label: 'Aprobados', value: misProyectos.filter(p => ['Aprobado', 'En ejecución', 'Finalizado'].includes(p.estado)).length, icon: 'ri-checkbox-circle-line', color: 'text-teal-600 bg-teal-50' },
            { label: 'Con correcciones', value: misProyectos.filter(p => p.estado === 'Con correcciones').length, icon: 'ri-edit-circle-line', color: 'text-orange-600 bg-orange-50' },
          ].map((s, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-lg p-4 flex items-center gap-3">
              <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${s.color}`}>
                <i className={`${s.icon} text-lg`} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800 leading-none">{s.value}</p>
                <p className="text-xs text-slate-500 mt-0.5">{s.label}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Projects list */}
      {misProyectos.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-16 h-16 flex items-center justify-center bg-slate-100 rounded-full mb-4">
            <i className="ri-file-text-line text-3xl text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-700 mb-2">Aún no tiene proyectos presentados</h3>
          <p className="text-slate-500 text-sm mb-6 max-w-sm">
            Presente su primera propuesta de investigación al Comité de Investigación e Interacción.
          </p>
          <button
            onClick={onNuevoProyecto}
            className="whitespace-nowrap flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-6 py-3 rounded-lg text-sm font-semibold transition-colors cursor-pointer"
          >
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line" /></span>
            Presentar mi primer proyecto
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <h3 className="text-base font-semibold text-slate-700 flex items-center gap-2">
            <span className="w-5 h-5 flex items-center justify-center"><i className="ri-folder-open-line" /></span>
            Mis proyectos presentados
          </h3>
          {misProyectos.map(p => {
            const evaluado = tieneEvaluaciones(p);
            const evals = p.evaluaciones || [];
            return (
              <div key={p.codigo} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                {/* Header */}
                <div className="p-5 flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <span className="font-mono text-xs font-bold text-teal-700 bg-teal-50 border border-teal-200 px-2 py-0.5 rounded">
                        {p.codigo}
                      </span>
                      <EstadoBadge estado={p.estado} />
                      {evaluado && (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-teal-700 bg-teal-50 border border-teal-200 px-2 py-0.5 rounded-full">
                          <span className="w-3 h-3 flex items-center justify-center"><i className="ri-check-double-line" /></span>
                          Evaluado
                        </span>
                      )}
                    </div>
                    <h4 className="text-base font-semibold text-slate-800 mb-1 leading-snug">{p.titulo}</h4>
                    <div className="flex flex-wrap gap-3 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-calendar-line" /></span>
                        Recibido: {p.fechaRecepcion}
                      </span>
                      {p.area && (
                        <span className="flex items-center gap-1">
                          <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-bookmark-line" /></span>
                          {p.area}
                        </span>
                      )}
                      {p.presupuesto && p.presupuesto !== '—' && (
                        <span className="flex items-center gap-1">
                          <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-money-dollar-circle-line" /></span>
                          {p.presupuesto}
                        </span>
                      )}
                    </div>
                  </div>

                </div>

                {/* Decision banner */}
                {p.decision && p.decision !== '—' && (
                  <div className={`px-5 py-3 border-t flex items-start gap-2 ${
                    p.estado === 'Aprobado' || p.estado === 'En ejecución'
                      ? 'bg-teal-50 border-teal-100'
                      : p.estado === 'Con correcciones'
                      ? 'bg-orange-50 border-orange-100'
                      : p.estado === 'No pertinente'
                      ? 'bg-red-50 border-red-100'
                      : 'bg-slate-50 border-slate-100'
                  }`}>
                    <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className={`text-sm ${
                        p.estado === 'Aprobado' || p.estado === 'En ejecución'
                          ? 'ri-checkbox-circle-line text-teal-600'
                          : p.estado === 'Con correcciones'
                          ? 'ri-edit-circle-line text-orange-600'
                          : 'ri-close-circle-line text-red-600'
                      }`} />
                    </span>
                    <div>
                      <p className="text-xs font-semibold text-slate-700">Decisión del Comité</p>
                      <p className="text-xs text-slate-600 mt-0.5">{p.decision}</p>
                      {p.fechaDecision && p.fechaDecision !== '—' && (
                        <p className="text-xs text-slate-400 mt-0.5">Emitida el {p.fechaDecision}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Evaluaciones del comité */}
                {evaluado && (
                  <div className="border-t border-slate-100 px-5 py-4 bg-slate-50">
                    <p className="text-xs font-semibold text-slate-600 mb-3 flex items-center gap-1.5">
                      <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-microscope-line" /></span>
                      Evaluaciones del Comité de Investigación e Interacción
                    </p>
                    {evals.length > 0 ? (
                      <div className="space-y-3">
                        {evals.map((ev, idx) => (
                          <div key={idx} className="bg-white border border-slate-200 rounded-lg p-3">
                            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                              <span className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                                <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-user-star-line" /></span>
                                {ev.evaluador}
                              </span>
                              <div className="flex items-center gap-2">
                                {ev.recomendacion && (
                                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${
                                    ev.recomendacion === 'Aprobado'
                                      ? 'bg-teal-50 text-teal-700 border-teal-200'
                                      : ev.recomendacion === 'No pertinente'
                                      ? 'bg-red-50 text-red-700 border-red-200'
                                      : 'bg-orange-50 text-orange-700 border-orange-200'
                                  }`}>
                                    {ev.recomendacion}
                                  </span>
                                )}
                                <span className="text-xs text-slate-400">{ev.fecha}</span>
                              </div>
                            </div>
                            {ev.observaciones && (
                              <p className="text-xs text-slate-600 leading-relaxed">{ev.observaciones}</p>
                            )}
                            {/* Scores summary */}
                            {ev.scores && Object.keys(ev.scores).length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-1.5">
                                {Object.entries(ev.scores).slice(0, 4).map(([k, v]) => (
                                  <span key={k} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                                    {k}: <strong>{v}</strong>
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-500 italic">El Comité ha registrado evaluación. Consulte el resultado en la decisión oficial.</p>
                    )}
                  </div>
                )}

                {/* Pending notice */}
                {!evaluado && (p.estado === 'Pendiente evaluación' || p.estado === 'En evaluación') && (
                  <div className="border-t border-slate-100 px-5 py-3 bg-amber-50 flex items-center gap-2">
                    <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-amber-600 text-sm" />
                    </span>
                    <p className="text-xs text-amber-700">
                      Su proyecto está siendo revisado por el Comité de Investigación e Interacción. Recibirá una notificación cuando se emita el dictamen.
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
